// Read behavior config from the DOM. Config travels as `data-tc-*` attributes on
// the Webflow-authored banner element — nothing is fetched. See the schema in
// WEBFLOW_NATIVE_REDESIGN.md.

import type { BannerType, Category, RuntimeConfig } from './types';

const ALL_CATEGORIES: Category[] = ['necessary', 'preferences', 'statistics', 'marketing'];
const VALID_TYPES: BannerType[] = ['opt-in', 'opt-out', 'info'];

export function findBanner(): HTMLElement | null {
  try {
    return document.querySelector('[data-tc-banner]') as HTMLElement | null;
  } catch {
    return null;
  }
}

export function findPreferences(): HTMLElement | null {
  try {
    return document.querySelector('[data-tc-preferences]') as HTMLElement | null;
  } catch {
    return null;
  }
}

export function readConfig(banner: HTMLElement): RuntimeConfig {
  const type = pickType(banner.getAttribute('data-tc-type'));
  const expiryDays = clampInt(banner.getAttribute('data-tc-expiry-days'), 90, 1, 730);
  const version = clampInt(banner.getAttribute('data-tc-version'), 1, 0, 1_000_000);
  const gcm = banner.getAttribute('data-tc-gcm') !== 'off';
  const gpc = banner.getAttribute('data-tc-gpc') !== 'off';
  const siteKey = banner.getAttribute('data-tc-site') || undefined;

  return {
    type,
    expiryDays,
    version,
    gcm,
    gpc,
    cookieName: 'tc_consent',
    categories: detectCategories(),
    siteKey,
  };
}

/** Categories actually offered, derived from the `[data-tc-category]` toggles. */
function detectCategories(): Category[] {
  const found = new Set<Category>(['necessary']);
  try {
    const nodes = document.querySelectorAll('[data-tc-category]');
    nodes.forEach((n) => {
      const c = (n as HTMLElement).getAttribute('data-tc-category') as Category | null;
      if (c && ALL_CATEGORIES.includes(c)) found.add(c);
    });
  } catch {
    /* fall through to defaults */
  }
  // Preserve canonical order; if only `necessary` was found, offer the full set
  // so an info/simple banner still maps GCM signals sensibly.
  const ordered = ALL_CATEGORIES.filter((c) => found.has(c));
  return ordered.length > 1 ? ordered : ALL_CATEGORIES.slice();
}

function pickType(raw: string | null): BannerType {
  return raw && (VALID_TYPES as string[]).includes(raw) ? (raw as BannerType) : 'opt-in';
}

function clampInt(raw: string | null, fallback: number, min: number, max: number): number {
  const n = raw ? parseInt(raw, 10) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}
